import { SignatureV4 } from '@smithy/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import type { AwsCredentialIdentityProvider } from '@aws-sdk/types';

/**
 * Creates a fetch wrapper that signs requests with AWS SigV4 for AppSync.
 * Used for the Amazon Approvals API which requires IAM auth instead of Midway Bearer tokens.
 */
export function createSigV4Fetch(credentialProvider: AwsCredentialIdentityProvider, region: string): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
    const credentials = await credentialProvider();

    const signer = new SignatureV4({
      credentials,
      region,
      service: 'appsync',
      sha256: Sha256,
    });

    const signed = await signer.sign({
      method: init?.method ?? 'POST',
      hostname: url.hostname,
      path: url.pathname,
      protocol: url.protocol,
      headers: {
        'Content-Type': 'application/json',
        host: url.hostname,
      },
      body: init?.body as string | undefined,
    });

    // Use Headers so case-insensitive names (e.g. Apollo's `content-type` and the signer's
    // `Content-Type`) collapse to a single value instead of round-tripping through an object
    // spread that keeps both and lets fetch concatenate them into `application/json, application/json`.
    const mergedHeaders = new Headers(init?.headers);
    for (const [name, value] of Object.entries(signed.headers as Record<string, string>)) {
      mergedHeaders.set(name, value);
    }
    return globalThis.fetch(input, { ...init, headers: mergedHeaders });
  };
}
